import { Grid, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { GetProps } from 'react-redux';
import * as yup from 'yup';

import { AccountFragmentFragment, DistanceUnitType, FloorType, GrainType } from '../../api/graphql';
import { ContainerTypeLegacy, getDistanceUnitLabel, getHasUserSelectedMeterUnit } from '../../util';
import { yup_latlng } from '../util/form2/LocationPicker';
import { yup_floor_type } from '../util/form2/SelectFloorType';
import { yup_grain_type } from '../util/form2/SelectGrainType';
import { GrainBinContainerTypeWizard, GrainBinFields } from './PickGrainContainerStep';
import { WizardStep } from './WizardStep';

const useStyles = makeStyles((theme: Theme) => ({
  grid: {
    minWidth: 300,
    maxWidth: 400,
    margin: 20,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 600,
    },
  },
  location: {
    height: 400,
  },
  centered: {
    textAlign: 'center',
  },
}));

const hasUserOptedMeterUnit = getHasUserSelectedMeterUnit();

export const validationSchema = yup.object().shape({
  alias: yup
    .string()
    .label('Bin Name')
    .required(),
  grain_type: yup_grain_type.label('Grain Type').required(),
  height_ft: yup
    .number()
    .typeError('A number is required')
    .positive()
    .label('Eave Height')
    .required()
    .max(hasUserOptedMeterUnit ? 60 : 200),
  diameter_ft: yup
    .number()
    .typeError('A number is required')
    .positive()
    .label('Bin Diameter')
    .required()
    .max(hasUserOptedMeterUnit ? 60 : 200),
  location: yup_latlng.label('Location'),
  has_grain_spreader: yup.boolean(),
  floor_type: yup_floor_type.label('Floor Type').required(),
  container_type: yup
    .string()
    .oneOf(
      [
        GrainBinContainerTypeWizard.STEEL_BIN,
        GrainBinContainerTypeWizard.CONCRETE_BIN,
        GrainBinContainerTypeWizard.HOPPER_BOTTOM_BIN,
      ],
      'Invalid container type'
    ),
  hopper_bottom_angle_degrees: yup
    .number()
    .label('Hopper Bottom Angle')
    .when('container_type', {
      is: GrainBinContainerTypeWizard.HOPPER_BOTTOM_BIN,
      then: yup
        .number()
        .required()
        .min(1)
        .max(90),
      otherwise: yup.number().nullable(),
    }),
  plenum_height_ft: yup
    .number()
    .positive()
    .nullable(),
});

export const CreateGrainContainerStep = ({
  validationSchema,
}: Partial<GetProps<typeof WizardStep>>) => {
  const classes = useStyles();

  return (
    <WizardStep title="" validationSchema={validationSchema}>
      <Grid
        container
        direction="row"
        alignContent="flex-start"
        alignItems="center"
        justify="center"
        spacing={2}
        className={classes.grid}
      >
        <Grid item xs={12}>
          <Typography variant="h5" className={classes.centered}>
            Create New Grain Bin
          </Typography>
        </Grid>
        <GrainBinFields />
      </Grid>
    </WizardStep>
  );
};
